import { useEffect, useMemo, useState } from 'react';
import type { AppLanguage } from '@/i18n/translations';
import { useLocale } from '@/i18n/LocaleProvider';
import { useTranslation } from '@/i18n/useTranslation';
import {
  fetchPlanningInfo,
  type PlanningInfoResponse,
} from '@/lib/api/assessment';
import {
  normalizePlanningView,
  type NormalizedPlanningView,
} from '@/lib/api/planning-info-mapper';
import { resolvePlanningParcelDisplay } from '@/lib/planning-parcel-display';
import {
  mergeAssessmentPlanningCache,
  peekAssessmentPlanningCache,
} from '@/lib/assessment-planning-cache';

export function usePlanningInfoReport(
  sessionId: string | undefined,
  languageOverride?: AppLanguage,
) {
  const t = useTranslation();
  const { language: contextLanguage } = useLocale();
  const language = languageOverride ?? contextLanguage;

  const cachedPlanning = sessionId
    ? peekAssessmentPlanningCache(sessionId)?.planningInfo
    : null;

  const [planningInfo, setPlanningInfo] = useState<PlanningInfoResponse | null>(
    cachedPlanning ?? null,
  );
  const [loading, setLoading] = useState(Boolean(sessionId) && !cachedPlanning);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError(t.planningInfo.missingSession);
      return;
    }

    const cached = peekAssessmentPlanningCache(sessionId)?.planningInfo;
    if (cached) {
      setPlanningInfo(cached);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchPlanningInfo(sessionId, { extractTakanon: true })
      .then((data) => {
        if (cancelled) return;
        setPlanningInfo(data);
        mergeAssessmentPlanningCache(sessionId, { planningInfo: data });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t.planningInfo.errorTitle);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, t.planningInfo.errorTitle, t.planningInfo.missingSession]);

  const view = useMemo(
    () =>
      planningInfo
        ? normalizePlanningView(planningInfo.sections_json, language)
        : null,
    [planningInfo, language],
  );

  const parcelDisplay = useMemo(() => {
    if (!planningInfo) return null;
    return resolvePlanningParcelDisplay(
      planningInfo,
      { migrash: t.map.fields.migrash, planPrimary: t.map.fields.planPrimary },
      language,
    );
  }, [planningInfo, t.map.fields.migrash, t.map.fields.planPrimary, language]);

  const documentRef = useMemo(() => {
    if (!planningInfo) return '';
    const stamp = planningInfo.fetched_at
      ? new Date(planningInfo.fetched_at).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    const suffix =
      parcelDisplay?.documentRefSuffix ?? sessionId?.slice(0, 8) ?? 'report';
    return `MGIS-${suffix.replace(/\s+/g, '-')}-${stamp}`;
  }, [planningInfo, parcelDisplay?.documentRefSuffix, sessionId]);

  const parcelLine = parcelDisplay?.parcelLine;

  return {
    planningInfo,
    view,
    loading,
    error,
    documentRef,
    parcelLine,
    parcelDisplay,
    language,
    labels: {
      title: t.planningInfo.title,
      coverBadge: t.planningInfo.report.coverBadge,
      reportCoverSubtitle: t.planningInfo.reportCoverSubtitle,
      reportDate: t.planningInfo.reportDate,
      requestType: t.planningInfo.requestType,
      reportDocumentRef: t.planningInfo.reportDocumentRef,
      informationalNotice: t.planningInfo.reportInformationalNotice,
      pdfProducedBy: t.planningInfo.pdfProducedBy,
      reportLoading: t.planningInfo.reportLoading,
      verifySourceLabel: t.planningInfo.verifySource,
      openPdfOnPageLabel: t.planningInfo.openPdfOnPage,
      extractionDraftLabel: t.planningInfo.extractionSectionDraftNotice,
    },
  };
}

export type PlanningInfoReportViewProps = {
  view: NormalizedPlanningView;
  parcelLine?: string;
  documentRef: string;
  labels: ReturnType<typeof usePlanningInfoReport>['labels'];
  dir: 'rtl' | 'ltr';
};
